import { useQueries } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import { getPageOcrLinesApiV1OcrSnipLinesPost } from "../../api/generated/sdk.gen";
import {
  OcrLineSchema,
  OcrLinesResponseSchema,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

export type OcrLine = OcrLineSchema;

export type PageOcrLines = {
  page: number;
  status: OcrLinesResponseSchema["status"];
  lines: OcrLine[];
};

/**
 * Fetch OCR lines + bounding boxes for a set of pages of one file (MDRS-134).
 *
 * Scanned PDFs have no browser text layer, so semantic-search source
 * highlighting matches the cited snippet against these stored OCR lines and
 * draws highlight rectangles over the matching ones. One query per distinct
 * page (deduped), cached — reopening the same source is instant.
 */
export const useOcrLinesForPages = (
  caseNumber: string | undefined,
  fileName: string | undefined,
  pages: number[]
): PageOcrLines[] => {
  const uniquePages = Array.from(new Set(pages)).sort((a, b) => a - b);

  const results = useQueries({
    queries: uniquePages.map((page) => ({
      queryKey: [CLIENT_KEYS.OCR_LINES, caseNumber, fileName, page],
      enabled: Boolean(caseNumber && fileName && page > 0),
      // OCR layout is immutable once generated; cache aggressively.
      staleTime: 5 * 60 * 1000,
      queryFn: async (): Promise<PageOcrLines> => {
        const response = await getPageOcrLinesApiV1OcrSnipLinesPost({
          client: apiClient,
          body: {
            case_number: caseNumber as string,
            file_name: fileName as string,
            page_number: page,
          },
        });
        if ("error" in response && response.error) {
          throw new Error(
            buildApiErrorMessage("Failed to fetch OCR lines", response.error)
          );
        }
        const data = response.data as OcrLinesResponseSchema;
        return {
          page,
          status: data.status,
          lines: data.lines ?? [],
        };
      },
    })),
  });

  return results.map((r, i) => ({
    page: uniquePages[i],
    status: r.data?.status ?? "processing",
    lines: r.data?.lines ?? [],
  }));
};
